import { useEffect, useRef, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Stack,
	Typography,
} from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import * as XLSX from "xlsx";
import { GlobalState } from "context/GlobalContext";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
import { UPLOAD_TYPES, isMboLfsQtyViewer } from "../data";
import { Panel, outlinedButtonSx, primaryButtonSx } from "../sharedStyles";
import { buildUploadPayload, releaseParsedFile, startFileParse } from "./uploadFileParser";

const ACCEPTED_EXTENSIONS = [".xlsx", ".xls", ".csv"];

const formatUploadAt = (value) => {
	if (!value) return "";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return "";
	return date.toLocaleString("en-IN", {
		day: "2-digit",
		month: "short",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
};

function UploadCard({ uploadType, state, lastUpload, onFile, onClear, onUpload, onDownloadTemplate, readOnly }) {
	const inputRef = useRef(null);
	const [isDragOver, setIsDragOver] = useState(false);
	const { file, rowCount = 0, missing = [], reading, uploading, uploaded, error } = state;

	useEffect(() => {
		if (!file && inputRef.current) inputRef.current.value = "";
	}, [file]);

	const handleDrop = (e) => {
		e.preventDefault();
		setIsDragOver(false);
		onFile(uploadType.key, e.dataTransfer?.files?.[0]);
	};

	return (
		<Panel
			sx={{
				p: 2,
				height: "100%",
				minWidth: 0,
				width: "100%",
				boxSizing: "border-box",
				display: "flex",
				flexDirection: "column",
				overflow: "hidden",
			}}
		>
			<Stack direction="row" spacing={1.25} alignItems="flex-start" mb={1.5}>
				<Box
					sx={{
						width: 36,
						height: 36,
						borderRadius: 1.5,
						display: "grid",
						placeItems: "center",
						bgcolor: "#f3f4f6",
						color: "#1f2937",
						flexShrink: 0,
						"& .MuiSvgIcon-root": { fontSize: 20 },
					}}
				>
					{uploadType.icon}
				</Box>
				<Box minWidth={0} flex={1}>
					<Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
						<Typography variant="subtitle2" fontWeight={700} color="#111827">
							{uploadType.name}
						</Typography>
						{uploadType.group && (
							<Chip
								size="small"
								label={uploadType.group}
								sx={{
									height: 20,
									fontSize: "0.65rem",
									fontWeight: 700,
									letterSpacing: 0.2,
									bgcolor: "#111827",
									color: "#fff",
								}}
							/>
						)}
					</Stack>
					<Typography variant="caption" color="text.secondary" display="block">
						{uploadType.description}
					</Typography>
					{lastUpload?.uploaded_at && (
						<Typography variant="caption" fontWeight={600} color="#1f2937" display="block">
							Latest upload {formatUploadAt(lastUpload.uploaded_at)}
						</Typography>
					)}
				</Box>
				{uploaded && (
					<Chip
						size="small"
						icon={<CheckCircleOutlineIcon sx={{ fontSize: "14px !important" }} />}
						label="Uploaded"
						sx={{
							height: 24,
							fontWeight: 600,
							fontSize: "0.7rem",
							bgcolor: "#f0fdf4",
							color: "#15803d",
						}}
					/>
				)}
			</Stack>

			<Stack direction="row" flexWrap="wrap" gap={0.5} mb={1.5}>
				{uploadType.columns.map((col) => (
					<Chip
						key={col.key}
						size="small"
						label={col.label}
						sx={{
							height: 22,
							fontSize: "0.65rem",
							fontWeight: 600,
							bgcolor: "#f9fafb",
							border: "1px solid #e5e7eb",
							color: "#374151",
						}}
					/>
				))}
			</Stack>

			<Box
				onDragOver={(e) => {
					e.preventDefault();
					if (readOnly) return;
					setIsDragOver(true);
				}}
				onDragLeave={() => setIsDragOver(false)}
				onDrop={(e) => {
					e.preventDefault();
					if (readOnly) return;
					handleDrop(e);
				}}
				onClick={() => !readOnly && !uploading && !reading && inputRef.current?.click()}
				sx={{
					border: "1.5px dashed",
					borderColor: isDragOver
						? "#1f2937"
						: file
							? "rgba(21, 128, 61, 0.45)"
							: "#d1d5db",
					borderRadius: 1.5,
					px: 1.5,
					py: 2,
					textAlign: "center",
					cursor: readOnly || uploading || reading ? "not-allowed" : "pointer",
					bgcolor: isDragOver
						? "rgba(31, 41, 55, 0.04)"
						: file
							? "rgba(21, 128, 61, 0.04)"
							: "#fafafa",
					mb: 1.5,
					flex: 1,
				}}
			>
				<input
					ref={inputRef}
					type="file"
					hidden
					disabled={readOnly}
					accept=".xlsx,.xls,.csv"
					onChange={(e) => {
						const nextFile = e.target.files?.[0];
						e.target.value = "";
						onFile(uploadType.key, nextFile);
					}}
				/>
				{readOnly ? (
					<Stack spacing={0.35} alignItems="center">
						<CloudUploadOutlinedIcon sx={{ color: "#9ca3af", fontSize: 24 }} />
						<Typography variant="caption" fontWeight={600} color="#374151">
							Upload is not available
						</Typography>
						<Typography variant="caption" color="text.secondary">
							Viewer access
						</Typography>
					</Stack>
				) : file ? (
					<Stack spacing={0.5} alignItems="center">
						{reading ? (
							<CircularProgress size={22} />
						) : (
							<InsertDriveFileOutlinedIcon sx={{ color: "#15803d", fontSize: 22 }} />
						)}
						<Typography
							variant="caption"
							fontWeight={700}
							color="#111827"
							sx={{ wordBreak: "break-all" }}
						>
							{file.name}
						</Typography>
						<Typography variant="caption" color="text.secondary">
							{reading
								? rowCount
									? `Reading... ${rowCount.toLocaleString("en-IN")} rows ready`
									: "Reading file..."
								: `${rowCount.toLocaleString("en-IN")} rows`}
						</Typography>
					</Stack>
				) : (
					<Stack spacing={0.35} alignItems="center">
						<CloudUploadOutlinedIcon sx={{ color: "#9ca3af", fontSize: 24 }} />
						<Typography variant="caption" fontWeight={600} color="#374151">
							Drop file or browse
						</Typography>
						<Typography variant="caption" color="text.secondary">
							.xlsx / .xls / .csv
						</Typography>
					</Stack>
				)}
			</Box>

			{error && (
				<Alert severity="error" sx={{ mb: 1.25, py: 0, borderRadius: 1.5 }}>
					{error}
				</Alert>
			)}
			{missing.length > 0 && (
				<Alert severity="warning" sx={{ mb: 1.25, py: 0, borderRadius: 1.5 }}>
					Missing: {missing.join(", ")}
				</Alert>
			)}

			<Stack
				direction="row"
				spacing={0.75}
				flexWrap="wrap"
				useFlexGap
				sx={{ mt: "auto", width: "100%", minWidth: 0 }}
			>
				<Button
					size="small"
					variant="outlined"
					startIcon={<DownloadOutlinedIcon />}
					onClick={() => onDownloadTemplate(uploadType)}
					sx={{ ...outlinedButtonSx, height: 32, fontSize: "0.75rem" }}
				>
					Template
				</Button>
				{file && !readOnly && (
					<Button
						size="small"
						variant="outlined"
						startIcon={<DeleteOutlineIcon />}
						onClick={() => onClear(uploadType.key)}
						sx={{ ...outlinedButtonSx, height: 32, fontSize: "0.75rem" }}
					>
						Clear
					</Button>
				)}
				{readOnly ? null : (
				<Button
					size="small"
					variant="contained"
					disabled={!file || missing.length > 0 || uploading || reading || !rowCount}
					onClick={() => onUpload(uploadType.key)}
					startIcon={
						uploading ? (
							<CircularProgress size={12} color="inherit" />
						) : (
							<CloudUploadOutlinedIcon />
						)
					}
					sx={{ ...primaryButtonSx, height: 32, fontSize: "0.75rem" }}
				>
					{uploading ? "Saving..." : "Upload"}
				</Button>
				)}
			</Stack>
		</Panel>
	);
}

const emptyState = () => ({
	file: null,
	rowCount: 0,
	missing: [],
	reading: false,
	uploading: false,
	uploaded: false,
	error: "",
});

function Upload() {
	const { loginUser, setSnackBar, setSnackBarOpen } = GlobalState();
	const viewer = isMboLfsQtyViewer(loginUser);
	const showSnack = (message, severity = "info") => {
		setSnackBarOpen(true);
		setSnackBar((prev) => ({ ...prev, message, severity }));
	};

	const [states, setStates] = useState(() =>
		Object.fromEntries(UPLOAD_TYPES.map((type) => [type.key, emptyState()]))
	);
	const [latestUploads, setLatestUploads] = useState({});
	const parsedFiles = useRef({});
	const parseTokens = useRef({});

	const loadLatestUploads = async () => {
		try {
			const res = await pythonVmInstance.get("/mbo-lfs-qty-reco/uploads");
			setLatestUploads(res?.data?.result?.uploads || {});
		} catch {
			setLatestUploads({});
		}
	};

	useEffect(() => {
		loadLatestUploads();
	}, []);

	const updateState = (key, patch) => {
		setStates((prev) => ({
			...prev,
			[key]: { ...prev[key], ...patch },
		}));
	};

	const handleFile = async (typeKey, file) => {
		if (viewer || !file) return;
		const uploadType = UPLOAD_TYPES.find((t) => t.key === typeKey);
		const lower = file.name.toLowerCase();
		const valid = ACCEPTED_EXTENSIONS.some((ext) => lower.endsWith(ext));
		if (!valid) {
			showSnack("Please upload Excel (.xlsx/.xls) or CSV", "error");
			return;
		}

		updateState(typeKey, {
			file,
			rowCount: 0,
			missing: [],
			reading: true,
			uploaded: false,
			error: "",
		});
		const token = Symbol(typeKey);
		parseTokens.current[typeKey] = token;
		const previousId = parsedFiles.current[typeKey];
		if (previousId) releaseParsedFile(previousId);
		delete parsedFiles.current[typeKey];

		try {
			await new Promise((resolve) => {
				setTimeout(resolve, 0);
			});
			const buffer = await file.arrayBuffer();
			const columns = uploadType.columns.map((col) => ({
				key: col.key,
				label: col.label,
				required: col.required,
			}));
			const { id, promise } = startFileParse(buffer, columns);
			const result = await promise;
			if (parseTokens.current[typeKey] !== token) {
				releaseParsedFile(id);
				return;
			}
			parsedFiles.current[typeKey] = id;
			updateState(typeKey, {
				reading: false,
				rowCount: result.rowCount || 0,
				missing: result.missing || [],
				uploaded: false,
				error: result.rowCount ? "" : "No data rows found",
			});
			if (result.missing?.length) {
				showSnack(`${uploadType.name}: missing ${result.missing.join(", ")}`, "warning");
			} else if (!result.rowCount) {
				showSnack(`${uploadType.name}: no data rows found`, "warning");
			} else {
				showSnack(`${uploadType.name}: ${result.rowCount.toLocaleString("en-IN")} rows ready`, "success");
			}
		} catch (err) {
			if (parseTokens.current[typeKey] !== token) return;
			updateState(typeKey, {
				...emptyState(),
				error: err?.message || "Failed to read file",
			});
			showSnack(err?.message || "Failed to read file", "error");
		}
	};

	const handleClear = (typeKey) => {
		parseTokens.current[typeKey] = Symbol(typeKey);
		const storeId = parsedFiles.current[typeKey];
		if (storeId) releaseParsedFile(storeId);
		delete parsedFiles.current[typeKey];
		updateState(typeKey, emptyState());
	};

	const handleDownloadTemplate = (uploadType) => {
		const sheetData = [
			uploadType.columns.map((col) => col.label),
			uploadType.sample,
		];
		const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
		const workbook = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(workbook, worksheet, uploadType.name);
		XLSX.writeFile(workbook, `mbo_lfs_${uploadType.key}_template.xlsx`);
	};

	const handleUpload = async (typeKey) => {
		if (viewer) return;
		const uploadType = UPLOAD_TYPES.find((t) => t.key === typeKey);
		const state = states[typeKey];
		const storeId = parsedFiles.current[typeKey];
		if (!state?.file || !storeId || state.reading) return;
		if (state.missing.length) {
			showSnack("Fix missing columns before upload", "error");
			return;
		}

		updateState(typeKey, { uploading: true, error: "" });
		showSnack(`${uploadType.name}: saving ${Number(state.rowCount).toLocaleString("en-IN")} rows`, "info");
		try {
			await new Promise((resolve) => {
				setTimeout(resolve, 0);
			});
			const payload = await buildUploadPayload(storeId, typeKey);
			const res = await pythonVmInstance.post("/mbo-lfs-qty-reco/upload", payload, {
				headers: { "Content-Type": "application/json" },
				transformRequest: [(data) => data],
			});
			releaseParsedFile(storeId);
			delete parsedFiles.current[typeKey];
			updateState(typeKey, { ...emptyState(), uploaded: true });
			const stored = res?.data?.result?.rows ?? state.rowCount;
			showSnack(`${uploadType.name}: ${Number(stored).toLocaleString("en-IN")} rows stored`, "success");
			loadLatestUploads();
		} catch (err) {
			const data = err?.response?.data;
			const message =
				(typeof data === "string" && data.trim()) ||
				data?.message ||
				err?.message ||
				"Upload failed";
			updateState(typeKey, { uploading: false, uploaded: false, error: message });
			showSnack(message, "error");
		}
	};

	const uploadedCount = UPLOAD_TYPES.filter((t) => states[t.key]?.uploaded).length;

	return (
		<Box>
			<Panel sx={{ px: 2, py: 1.5, mb: 1.5 }}>
				<Stack
					direction={{ xs: "column", sm: "row" }}
					justifyContent="space-between"
					alignItems={{ xs: "flex-start", sm: "center" }}
					spacing={1}
				>
					<Box>
						<Typography variant="subtitle1" fontWeight={700} color="#111827">
							Multi-file Upload
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Each data type has its own columns — upload CUS-SOH and GIT files separately.
						</Typography>
					</Box>
					<Chip
						label={`${uploadedCount} / ${UPLOAD_TYPES.length} uploaded`}
						size="small"
						sx={{ height: 26, fontWeight: 600, bgcolor: "#f3f4f6" }}
					/>
				</Stack>
			</Panel>

			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: {
						xs: "minmax(0, 1fr)",
						md: "repeat(2, minmax(0, 1fr))",
						xl: "repeat(3, minmax(0, 1fr))",
					},
					gap: 1.5,
					mb: 1.5,
					alignItems: "stretch",
				}}
			>
				{UPLOAD_TYPES.map((uploadType) => (
					<Box key={uploadType.key} sx={{ minWidth: 0 }}>
						<UploadCard
							uploadType={uploadType}
							state={states[uploadType.key]}
							lastUpload={latestUploads[uploadType.key]}
							onFile={handleFile}
							onClear={handleClear}
							onUpload={handleUpload}
							onDownloadTemplate={handleDownloadTemplate}
							readOnly={viewer}
						/>
					</Box>
				))}
			</Box>
		</Box>
	);
}

export default Upload;
